// RITA-1547 — Seite „Vertrag erstellen › Bearbeiten“ (Route /agent/:number/vertrag-bearbeiten, nur
// Super-Admins). Zwei Wege hinein:
//   - `?neu=1` (Knopf „Bearbeiten“ im Fenster „Vertrag erstellen“): Das PRM wählt die Vorlagen wie in
//     RITA-1543 und setzt die gespeicherten Daten des Beraters als Text ein. Ein vorhandener Entwurf bleibt,
//     bis der neue gespeichert wird (F2). Ersetzt wird nur der Entwurf, den die Rückfrage B gezeigt hat
//     (`&ersetzt=<Revision>`); hat inzwischen jemand anderes gespeichert oder gibt es einen Entwurf, den
//     niemand bestätigt hat, warnt das Speichern (CONFLICT, Bild G), statt still zu ersetzen.
//   - ohne `neu` („Weiterbearbeiten“): der gespeicherte Entwurf. Haben sich Vorlage oder Berater-Daten
//     seitdem geändert, fragt das PRM „Entwurf behalten“ oder „Neu beginnen“ (Bild E).
// `?dokument=vertrag` öffnet den Reiter Vertrag, sonst das Anschreiben.
// Hinaus führt neben „Zurück zum Berater“ seit RITA-1549 auch „Drucken“: Es druckt den sichtbaren Stand, erledigt
// bei vollständiger Auswahl den Entwurf und kehrt ohne Verlassen-Rückfrage zum Bereich „Vertrag“ des Beraters
// zurück (Bild C, F3); die Karte dort lädt Entwurf und Aktionsprotokoll neu.
//
// RITA-1552 (Skizze Rücklauf, Bild E; F8 und Entscheidung E6 von Julia freigegeben) — `?vorgang=ruecksendung` öffnet die
// Seite für den eigenen Entwurf der Rücksendung (ohne Parameter wie bisher der Vertrag): Vorlagen nur der Art Anschreiben
// Rücksendung (`kinds`), der Entwurf der Rücksendung (getRuecksendungsentwurfMitDokumenten), ohne passende Vorlage der
// Hinweis aus Bild D c; nach dem Speichern bleibt `?vorgang=ruecksendung` in der Adresse, damit ein Neuladen wieder den
// Entwurf der Rücksendung öffnet. Jede Sitzung gehört zu einem Berater UND einem Vorgang (eigener Schlüssel), damit ein
// Sprung im Verlauf zwischen beiden Vorgängen desselben Beraters nicht den Inhalt des einen unter dem anderen speichert.
// Vertragsbezug (E6, Befund F1 aus dem Review von MR 1): Der Druck der Rücksendung muss den „Gehört zu“-Vertrag
// mitschicken. Die Seite bestimmt ihn selbst beim Laden jeder Sitzung aus dem Aktionsprotokoll – wie die Karte: der
// neueste im PRM gedruckte Vertrag (letzterVertragsdruck), sonst null (Fall B). Bewusst keine Übergabe aus der Karte
// über Adresse oder Router-Zustand: „Weiterbearbeiten“ aus der Entwurfszeile führt nicht über das Fenster, ein Neuladen
// verlöre den Zustand, und ein Wert in der Adresse wäre bei einem alten Lesezeichen veraltet. Der Vertrag gilt für die
// Sitzung wie im Fenster beim Öffnen; druckt jemand währenddessen einen neuen Vertrag, lehnt der Server ab
// (CONTRACT_CHANGED) und die Seite bestimmt ihn neu. Scheitert das Laden des Protokolls, scheitert das Laden der Seite
// („Erneut versuchen“) – ein stilles null führte zu einer falschen Ablehnung.
//
// RITA-1601 (Fund 128, Entscheidung Julia 04.11.2026) — `&ersetztVertrag=<Kennung>`: Kam die Seite über „Trotzdem neuen
// erstellen“ der Karte und „Bearbeiten“ im Fenster, ist das Ersetzen dieses Drucks mit Vertrag schon bestätigt; die
// Rückfrage „Schon ein Vertrag gedruckt“ vor einem Druck mit Vertrag entfällt dann für genau diesen Vertrag. Die
// Bestätigung gilt für die Sitzung des Beraters (auch über „Neu beginnen“) und bleibt nach dem Speichern in der Adresse,
// damit ein Neuladen nicht noch einmal fragt. Anders als beim Vertragsbezug der Rücksendung ist ein veralteter Wert hier
// unschädlich: Ist inzwischen ein anderer Vertrag der neueste, fragt die Seite wieder. Den Druckstand selbst lädt die Seite
// nicht beim Öffnen, sondern EntwurfBearbeitung beim Klick auf „Drucken“ frisch (`onProtokollLaden`) – auch wenn jemand
// anderes inzwischen gedruckt hat. RITA-1601 (Fund 63): Dazu bekommt die Seite den abweichenden Vertragsinhaber aus dem
// gespeicherten Berater für den Kasten über dem Text und die Rückfrage „Der Vertragsinhaber fehlt im Vertrag“.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import type { ContractCreationKind } from 'global-intern-types';
import { leseGespeichertesVertragsdokument } from '../../templates/contractEditor/model/lesen';
import {
  GET_VERTRAGSAUSWAHL_MIT_DOKUMENTEN,
  VertragsauswahlAntwort,
  VertragsauswahlVariablen,
} from '../api/vertragserstellung';
import {
  Entwurfsdokument,
  EntwurfsvorlageAntwort,
  GET_ENTWURFSVORLAGE,
  VertragsentwurfAntwort,
  VertragsentwurfVariablen,
} from '../api/vertragsentwurf';
import { Druckeintrag, DruckprotokollAntwort, DruckprotokollVariablen, GET_DRUCKPROTOKOLL } from '../api/vertragsdruck';
import EntwurfBearbeitung, { beraterAdresse, EntwurfStart, EntwurfsteilStart } from '../components/EntwurfBearbeitung';
import useBeraterVertragswerte from '../hooks/useBeraterVertragswerte';
import {
  beraterschluessel,
  eingesetzteBeraterwerte,
  geaenderteBeraterwerte,
  platzhalterAlsText,
} from '../services/entwurfsdokument';
import { angabeBezeichnung } from '../services/fehlendeAngaben';
import { Bearbeitername, vertragPlatzhalterwerte } from '../services/vertragPlatzhalterwerte';
import { letzterVertragsdruck } from '../services/vertragsbloecke';
import { ERNEUT_VERSUCHEN, kriterienText, vertragsartFehlt } from '../services/vertragserstellungTexte';
import {
  beraterdatenGeaendertText,
  keinEntwurfText,
  NEU_BEGINNEN,
  SEITE_LADEFEHLER,
  SEITE_WIRD_GELADEN,
  vertragsartFehltText,
  vorlageGeaendertText,
  vorlageGeloeschtText,
  ZURUECK_ZUM_BERATER,
} from '../services/vertragsentwurfTexte';
import { ERSETZT_VERTRAG_PARAMETER, Vorgang, VORGANG_PARAMETER, vorgangAusSuche } from '../services/vorgang';
import { VORGANG_KONFIG } from '../services/vorgangskonfiguration';

type Seitenstand =
  | { art: 'laedt' }
  | { art: 'fehler' }
  | { art: 'keinEntwurf' }
  | { art: 'keineVorlage'; meldung: string }
  | { art: 'bereit'; start: EntwurfStart; sitzung: number };

type Anfang = { start: EntwurfStart } | { meldung: string } | { keinEntwurf: true };

/**
 * Woher die Seite ihren Inhalt lädt: neu aus den Vorlagen (mit der Revision, die ersetzt werden darf) oder
 * der gespeicherte Entwurf.
 */
type Weg = { art: 'vorlagen'; ersetzt: string | null } | { art: 'entwurf' };

const OHNE_BEARBEITER: Bearbeitername = { vorname: null, nachname: null };

function agentNummer(wert: string | undefined): number {
  if (!wert || !/^\d+$/.test(wert)) return 0;
  const nummer = Number(wert);
  return Number.isSafeInteger(nummer) && nummer <= 2147483647 ? nummer : 0;
}

/** RITA-1601: Kennung eines Protokolleintrags aus der Adresse (`ersetztVertrag`); alles andere als eine positive ganze Zahl ist null. */
function eintragskennung(wert: string | null): number | null {
  if (!wert || !/^\d+$/.test(wert)) return null;
  const nummer = Number(wert);
  return Number.isSafeInteger(nummer) && nummer > 0 ? nummer : null;
}

/** Reiter beim Öffnen: bei der Rücksendung ihr einziges Dokument, beim Vertrag `dokument=vertrag` oder das Anschreiben. */
function anfangsreiter(vorgang: Vorgang, dokument: string | null): ContractCreationKind {
  if (vorgang === 'return') return 'return_letter';
  return dokument === 'vertrag' ? 'contract' : 'dispatch_letter';
}

function VertragsentwurfSitzung({ agentId, vorgang }: { agentId: number; vorgang: Vorgang }) {
  const [suche] = useSearchParams();
  const konfig = VORGANG_KONFIG[vorgang];
  const [anfang] = useState(() => ({
    neu: suche.get('neu') === '1',
    ersetzt: suche.get('ersetzt') || null,
    reiter: anfangsreiter(vorgang, suche.get('dokument')),
    // RITA-1601 (Fund 128): in der Karte bestätigter Druck mit Vertrag (siehe Kopf); nur beim Vertrag von Bedeutung.
    ersetztVertrag: vorgang === 'contract' ? eintragskennung(suche.get(ERSETZT_VERTRAG_PARAMETER)) : null,
  }));
  // RITA-1601: gilt für die ganze Sitzung des Beraters, auch über „Neu beginnen“; „Trotzdem drucken“ der Seite setzt es.
  const [bestaetigterVertragsdruck, setBestaetigterVertragsdruck] = useState<number | null>(anfang.ersetztVertrag);
  const navigate = useNavigate();
  const location = useLocation();
  const client = useApolloClient();
  const berater = useBeraterVertragswerte(agentId);
  const [heute] = useState(() => new Date());
  const [stand, setStand] = useState<Seitenstand>({ art: 'laedt' });
  const sitzung = useRef(0);
  const reiterRef = useRef(anfang.reiter);
  const begonnen = useRef(false);
  // „Erneut versuchen“ wiederholt, was fehlschlug – nach „Gespeicherten Stand laden“ also den Entwurf,
  // auch wenn die Seite mit `neu` geöffnet wurde.
  const letzterWeg = useRef<Weg>(anfang.neu ? { art: 'vorlagen', ersetzt: anfang.ersetzt } : { art: 'entwurf' });
  const sucheRef = useRef(location.search);
  sucheRef.current = location.search;
  // Antworten, die erst nach dem Ende dieser Sitzung eintreffen (Wechsel zu einem anderen Berater), verwirft sie:
  // Sonst setzten sie Stand oder Adresse, während schon die Sitzung des anderen Beraters läuft.
  const aktiv = useRef(true);
  useLayoutEffect(() => {
    aktiv.current = true;
    return () => {
      aktiv.current = false;
    };
  }, []);

  const werteFuer = useCallback(
    (bearbeiter: Bearbeitername) =>
      berater.berater && berater.kontext
        ? vertragPlatzhalterwerte(berater.berater, { ...berater.kontext, bearbeiter, heute })
        : null,
    [berater.berater, berater.kontext, heute],
  );

  /**
   * Aktionsprotokoll dieses Beraters frisch laden (nur Einträge zu ihm; `no-cache` wie die übrigen Abfragen der Seite;
   * wirft bei einem Ladefehler). RITA-1552 (E6): daraus der „Gehört zu“-Vertrag der Rücksendung – der neueste im PRM
   * gedruckte Vertrag wie in der Karte, null = keiner (Fall B). RITA-1601 (Fund 128): daraus beim Klick auf „Drucken“ der
   * Vertrag, den ein Druck mit Vertrag ersetzen würde (EntwurfBearbeitung).
   */
  const protokollLaden = useCallback(async (): Promise<Druckeintrag[]> => {
    const antwort = await client.query<DruckprotokollAntwort, DruckprotokollVariablen>({
      query: GET_DRUCKPROTOKOLL,
      variables: { agent_id: agentId },
      fetchPolicy: 'no-cache',
    });
    return antwort.data.getContractCreationPrintLog.filter((eintrag) => eintrag.agent_id === agentId);
  }, [agentId, client]);

  /** Vertragsbezug zum Anfang einer Sitzung: nur bei der Rücksendung geladen, beim Vertrag ohne Abfrage null. */
  const vertragsbezugZumStart = useCallback(
    (): Promise<number | null> =>
      vorgang === 'return'
        ? protokollLaden().then((eintraege) => letzterVertragsdruck(eintraege)?.id ?? null)
        : Promise.resolve(null),
    [vorgang, protokollLaden],
  );

  /**
   * Neu aus den Vorlagen. `ersetzt` ist die Revision, die die Person bestätigt hat (Rückfrage B bzw. der
   * geöffnete Entwurf bei Hinweis E); nur sie darf das Speichern ersetzen (F2). Bewusst keine eigene Abfrage
   * des aktuellen Stands: Sie würde einen nie gezeigten fremden Entwurf ohne Warnung ersetzbar machen.
   * RITA-1552: nur die Arten des Vorgangs – abgefragt (`kinds`) und genommen, auch wenn die Antwort mehr enthielte.
   */
  const ausVorlagen = useCallback(
    async (ersetzt: string | null): Promise<Anfang> => {
      const [auswahlAntwort, vertragsbezug] = await Promise.all([
        client.query<VertragsauswahlAntwort, VertragsauswahlVariablen>({
          query: GET_VERTRAGSAUSWAHL_MIT_DOKUMENTEN,
          variables: { agent_id: agentId, kinds: konfig.arten },
          fetchPolicy: 'no-cache',
        }),
        vertragsbezugZumStart(),
      ]);
      const auswahl = auswahlAntwort.data.getContractCreation;
      const werte = werteFuer({
        vorname: auswahl.editor_first_name ?? null,
        nachname: auswahl.editor_last_name ?? null,
      });
      if (!werte) throw new Error('Berater-Daten fehlen');
      const vorlagen: Array<{ teil: EntwurfsteilStart; vorlage: EntwurfsteilStart['dokument'] }> = [];
      auswahl.documents.forEach((eintrag) => {
        const { template } = eintrag;
        if (!konfig.arten.includes(eintrag.kind)) return;
        if (eintrag.status !== 'found' || !template || !template.updated_at) return;
        try {
          const { dokument } = leseGespeichertesVertragsdokument(template.document);
          vorlagen.push({
            vorlage: dokument,
            teil: {
              kind: eintrag.kind,
              herkunft: {
                template_id: template.id,
                template_name: template.name,
                template_updated_at: template.updated_at,
              },
              dokument: platzhalterAlsText(dokument, werte),
              beraterschluessel: beraterschluessel(dokument),
            },
          });
        } catch {
          // Vorlage ohne lesbares Dokument: wie im Fenster (F9) nicht bearbeitbar.
        }
      });
      if (vorlagen.length === 0) {
        return {
          // RITA-1572: Wie beim übrigen Hinweis nennt nur der Vertrag den Satz zum Entwurf (Rücksendung: Bild D c).
          meldung: vertragsartFehlt(auswahl)
            ? vertragsartFehltText(vorgang === 'contract' && ersetzt !== null)
            : konfig.keineVorlageText(berater.beraterName, kriterienText(auswahl), ersetzt !== null),
        };
      }
      return {
        start: {
          teile: vorlagen.map(({ teil }) => teil),
          advisorValues: eingesetzteBeraterwerte(
            vorlagen.map(({ vorlage }) => vorlage),
            werte,
          ),
          revision: ersetzt,
          gespeichert: null,
          hinweis: null,
          reiter: reiterRef.current,
          vorgang,
          vertragsbezug,
        },
      };
    },
    [agentId, client, werteFuer, berater.beraterName, konfig, vorgang, vertragsbezugZumStart],
  );

  /**
   * RITA-1600 (Fund 107): Welche Berater-Daten braucht ein Dokument des gespeicherten Entwurfs? Es ist schon Text; das
   * verrät nur seine Vorlage, und nur, solange sie seit dem Entwurf unverändert ist. Sonst – oder wenn sie sich nicht
   * laden lässt – null: Die Rückfrage „Beim Anlegen fehlten Angaben“ nennt dann wie bisher alle leeren Angaben.
   */
  const beraterschluesselAusVorlage = useCallback(
    async (eintrag: Entwurfsdokument): Promise<string[] | null> => {
      if (eintrag.template_status !== 'unchanged' || !eintrag.template_id || !eintrag.template_updated_at) return null;
      try {
        const antwort = await client.query<EntwurfsvorlageAntwort, { id: number }>({
          query: GET_ENTWURFSVORLAGE,
          variables: { id: eintrag.template_id },
          fetchPolicy: 'no-cache',
        });
        const vorlage = antwort.data.getContractTemplate;
        const gleicherStand =
          !!vorlage?.updated_at &&
          new Date(vorlage.updated_at).getTime() === new Date(eintrag.template_updated_at).getTime();
        return vorlage && gleicherStand
          ? beraterschluessel(leseGespeichertesVertragsdokument(vorlage.document).dokument)
          : null;
      } catch {
        return null;
      }
    },
    [client],
  );

  /**
   * Der gespeicherte Entwurf, mit Hinweis, wenn sich Vorlage oder Berater-Daten seitdem geändert haben. RITA-1552: der
   * Entwurf des Vorgangs (Vertrag bzw. Rücksendung), nur mit den Dokumenten seiner Arten.
   */
  const ausEntwurf = useCallback(async (): Promise<Anfang> => {
    // `no-cache` wie das Fenster (RITA-1543): Die großen Dokumente samt eingesetzter Berater-Daten bleiben
    // nicht für die ganze Sitzung im Apollo-Cache.
    const [antwort, vertragsbezug] = await Promise.all([
      client.query<VertragsentwurfAntwort, VertragsentwurfVariablen>({
        query: konfig.entwurf.abfrageMitDokumenten,
        variables: { agent_id: agentId },
        fetchPolicy: 'no-cache',
      }),
      vertragsbezugZumStart(),
    ]);
    const entwurf = antwort.data.getContractCreationDraft;
    if (!entwurf) return { keinEntwurf: true };
    const werte = werteFuer(OHNE_BEARBEITER);
    if (!werte) throw new Error('Berater-Daten fehlen');
    const zeilen: string[] = [];
    let geloescht = false;
    const dokumente = entwurf.documents.filter((eintrag) => konfig.arten.includes(eintrag.kind));
    // Nur bei mehreren Dokumenten nötig: Ein einzelnes braucht ohnehin alle gemerkten Angaben (null = alle).
    const schluessel =
      dokumente.length > 1 ? await Promise.all(dokumente.map(beraterschluesselAusVorlage)) : dokumente.map(() => null);
    const teile = dokumente.map((eintrag, index): EntwurfsteilStart => {
      const name = eintrag.template_name ?? '';
      if (eintrag.template_status === 'changed') {
        zeilen.push(vorlageGeaendertText(name, eintrag.template_current_updated_at ?? null));
      }
      if (eintrag.template_status === 'deleted') {
        zeilen.push(vorlageGeloeschtText(name));
        geloescht = true;
      }
      return {
        kind: eintrag.kind,
        herkunft: {
          template_id: eintrag.template_id ?? 0,
          template_name: name,
          template_updated_at: eintrag.template_updated_at ?? '',
        },
        dokument: leseGespeichertesVertragsdokument(eintrag.document).dokument,
        beraterschluessel: schluessel[index],
      };
    });
    const geaendert = geaenderteBeraterwerte(entwurf.advisor_values, werte);
    if (geaendert.length > 0) zeilen.push(beraterdatenGeaendertText(geaendert.map(angabeBezeichnung)));
    return {
      start: {
        teile,
        advisorValues: entwurf.advisor_values,
        revision: entwurf.revision,
        gespeichert: { am: entwurf.updated_at, von: entwurf.updated_by_name },
        hinweis: zeilen.length > 0 ? { zeilen, geloescht } : null,
        reiter: reiterRef.current,
        vorgang,
        vertragsbezug,
      },
    };
  }, [agentId, client, werteFuer, konfig, vorgang, vertragsbezugZumStart, beraterschluesselAusVorlage]);

  const anwenden = useCallback((ergebnis: Anfang) => {
    if ('start' in ergebnis) {
      sitzung.current += 1;
      setStand({ art: 'bereit', start: ergebnis.start, sitzung: sitzung.current });
    } else if ('meldung' in ergebnis) {
      setStand({ art: 'keineVorlage', meldung: ergebnis.meldung });
    } else {
      setStand({ art: 'keinEntwurf' });
    }
  }, []);

  /**
   * Zeigt die Seite den gespeicherten Entwurf (geladen oder gerade gespeichert), öffnet ein Neuladen ihn statt
   * wieder die Vorlagen; die bestätigte Revision aus `ersetzt` gilt dann nicht mehr. RITA-1552: Die Rücksendung behält
   * `?vorgang=ruecksendung` (sonst öffnete ein Neuladen den Entwurf des Vertrags).
   */
  const adresseZumEntwurf = useCallback(() => {
    const aktuell = new URLSearchParams(sucheRef.current);
    if (!aktuell.has('neu') && !aktuell.has('ersetzt')) return;
    const ziel = new URLSearchParams(
      konfig.suche || `dokument=${anfang.reiter === 'contract' ? 'vertrag' : 'anschreiben'}`,
    );
    // RITA-1601: Die Bestätigung aus der Karte bleibt – ein Neuladen fragt nicht noch einmal nach demselben Vertrag.
    const ersetztVertrag = aktuell.get(ERSETZT_VERTRAG_PARAMETER);
    if (ersetztVertrag) ziel.set(ERSETZT_VERTRAG_PARAMETER, ersetztVertrag);
    navigate(`${location.pathname}?${ziel.toString()}`, { replace: true });
  }, [navigate, location.pathname, anfang.reiter, konfig.suche]);

  const laden = useCallback(
    (weg: Weg) => {
      letzterWeg.current = weg;
      setStand({ art: 'laedt' });
      (weg.art === 'vorlagen' ? ausVorlagen(weg.ersetzt) : ausEntwurf())
        .then((ergebnis) => {
          if (!aktiv.current) return;
          anwenden(ergebnis);
          if (weg.art === 'entwurf' && 'start' in ergebnis) adresseZumEntwurf();
        })
        .catch(() => {
          if (aktiv.current) setStand({ art: 'fehler' });
        });
    },
    [anwenden, ausVorlagen, ausEntwurf, adresseZumEntwurf],
  );

  useEffect(() => {
    if (begonnen.current || !berater.berater || !berater.kontext) return;
    begonnen.current = true;
    laden(anfang.neu ? { art: 'vorlagen', ersetzt: anfang.ersetzt } : { art: 'entwurf' });
  }, [berater.berater, berater.kontext, anfang.neu, anfang.ersetzt, laden]);

  const zumBerater = () => navigate(beraterAdresse(agentId));

  if (stand.art === 'bereit') {
    return (
      <EntwurfBearbeitung
        key={stand.sitzung}
        agentId={agentId}
        beraterName={berater.beraterName}
        start={stand.start}
        heute={heute}
        onStandLaden={() => laden({ art: 'entwurf' })}
        onNeuBeginnen={async (revision) => {
          letzterWeg.current = { art: 'vorlagen', ersetzt: revision };
          try {
            const ergebnis = await ausVorlagen(revision);
            if (!aktiv.current) return null;
            if ('meldung' in ergebnis) return ergebnis.meldung;
            anwenden(ergebnis);
            return null;
          } catch {
            if (aktiv.current) setStand({ art: 'fehler' });
            return null;
          }
        }}
        onGespeichert={adresseZumEntwurf}
        onProtokollLaden={protokollLaden}
        vertragsinhaber={(berater.berater?.business_owner ?? '').trim()}
        bestaetigterVertragsdruck={bestaetigterVertragsdruck}
        onVertragsdruckBestaetigt={setBestaetigterVertragsdruck}
      />
    );
  }

  let inhalt: React.ReactNode;
  if (berater.fehler || stand.art === 'fehler') {
    inhalt = (
      <Alert
        severity="error"
        action={
          <Button
            color="inherit"
            size="small"
            sx={{ textTransform: 'none' }}
            onClick={() => {
              berater.erneutLaden();
              begonnen.current = false;
              if (berater.berater && berater.kontext) {
                begonnen.current = true;
                laden(letzterWeg.current);
              }
            }}
          >
            {ERNEUT_VERSUCHEN}
          </Button>
        }
      >
        {SEITE_LADEFEHLER}
      </Alert>
    );
  } else if (stand.art === 'keinEntwurf') {
    inhalt = (
      <Alert severity="info" data-testid="entwurf-keiner">
        <Typography sx={{ mb: 1 }}>{keinEntwurfText(berater.beraterName, vorgang)}</Typography>
        <Button
          variant="contained"
          size="small"
          onClick={() => laden({ art: 'vorlagen', ersetzt: null })}
          sx={{ textTransform: 'none' }}
        >
          {NEU_BEGINNEN}
        </Button>
      </Alert>
    );
  } else if (stand.art === 'keineVorlage') {
    inhalt = (
      <Alert severity="warning" data-testid="entwurf-keine-vorlage-seite">
        {stand.meldung}
      </Alert>
    );
  } else {
    inhalt = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, justifyContent: 'center' }}>
        <CircularProgress size={20} />
        <Typography sx={{ color: 'text.secondary' }}>{SEITE_WIRD_GELADEN}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }} data-testid="entwurf-seite-zustand">
      {inhalt}
      <Button variant="outlined" sx={{ mt: 2, textTransform: 'none' }} onClick={zumBerater}>
        {ZURUECK_ZUM_BERATER}
      </Button>
    </Box>
  );
}

/**
 * Eine Sitzung der Seite gehört genau zu einem Berater. React Router behält die Seite bei, wenn nur `:number`
 * wechselt (etwa per Sprung im Verlauf des Browsers); deshalb hat jede Beraternummer ihren eigenen Schlüssel.
 * Sonst blieben Editor, Herkunft und Revision des vorigen Beraters stehen und würden unter der neuen Nummer
 * gespeichert (Kollegenreview !779, P1). Ein Adresswechsel desselben Beraters (nach dem Speichern) bleibt in
 * derselben Sitzung. RITA-1552: ebenso je Vorgang (`?vorgang=ruecksendung` oder Vertrag) – der Vorgang wechselt nur
 * mit einer neuen Sitzung.
 */
export default function VertragsentwurfPage() {
  const { number } = useParams<{ number: string }>();
  const [suche] = useSearchParams();
  const agentId = agentNummer(number);
  const vorgang = vorgangAusSuche(suche.get(VORGANG_PARAMETER));
  return <VertragsentwurfSitzung key={`${agentId}:${vorgang}`} agentId={agentId} vorgang={vorgang} />;
}
